import { Component } from 'react'

import classNames from 'classnames'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import qs from 'qs'
import { List } from 'semantic-ui-react'

import { toServerDate } from 'utils/dates'
import { downloadFileFromResponse } from 'utils/requests'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import Checkbox from 'components/Core/Checkbox/Checkbox'
import DatePicker from 'components/Core/DatePicker/DatePicker'
import Dropdown from 'components/Core/Dropdown/Dropdown'
import Modal from 'components/Core/Modal'

import { ExportDataIds } from '../ExportData.ids'
import styles from '../ExportDataModal.component.module.css'
import messages from '../ExportDataModal.messages'

const CUSTOM_ORDER = 'CUSTOM_ORDER'

// map to https://github.com/joor/web.php/blob/master/app/controllers/orders_controller.php#L8332
const STATUS_MAP = {
  ALL_NOTES: 'notes',
  PENDING: 'pending',
  APPROVED: 'approved',
  SHIPPED: 'shipped',
  CANCELLED: 'deleted',
}

export default class ExportAllDataModal extends Component {
  state = {
    options: this.props.statusFilter.reduce(
      (agg, status) => ({
        ...agg,
        [STATUS_MAP[status]]: true,
      }),
      {},
    ),
  }

  onSave = () => {
    const { options } = this.state

    const { handleExportAction, trackExportAllDataModalSubmitted } = this.props

    const startDate = toServerDate(options.start_date)
    const endDate = toServerDate(options.end_date)

    trackExportAllDataModalSubmitted({
      statusesDefault: this.getStatusesDefaults(),
      dateOptions: this.getDateOptions(),
      integrationsDropdownOptions: this.getDropdownOptions(),
      startDate,
      endDate,
      options,
    })

    handleExportAction(
      this.exportOrders,
      messages.downloadCompleted,
      messages.failedExport,
    )
  }

  getRequestData = () => {
    const { options } = this.state

    // At backend python, when we filter by all notes, the orders
    // with status returned are included for some reason,
    // so in order to obtain the same data at the export
    // we have to include returned orders
    if (options.notes) {
      options.returned = true
    }

    const data = {
      ...options,
    }

    // Copy all the properties to the `data` sub-object so the endpoint can select what it needs
    return qs.stringify({ ...data, data })
  }

  getStatusesOptions = () =>
    Object.values(STATUS_MAP).map((option) => ({
      value: option,
      text: this.formatStatus(option),
    }))

  getStatusesDefaults = () =>
    Object.values(STATUS_MAP).filter((option) => this.state.options[option])

  getDropdownOptions = () => [
    ...this.props.orderExportIntegrations
      .filter((option) => option.integrationType !== CUSTOM_ORDER)
      .map((option) => ({
        value: option.id,
        text: option.name,
        className: 'notranslate',
      })),
    {
      value: toGlobalId('OrderExportIntegration', '0'),
      text: messages.basicExport,
    },
  ]

  getDateOptions = () => [
    { value: 1, text: 'Complete Ship Date' },
    { value: 2, text: 'Created Date' },
    { value: 3, text: 'Modified Date' },
  ]

  setDate = (date) => {
    this.setState({
      options: {
        ...this.state.options,
        start_date: toServerDate(date.startDate),
        end_date: toServerDate(date.endDate),
      },
    })
  }

  setOrderExport = (e, { value }) => {
    const { id: orderExportIntegrationId } = fromGlobalId(value)
    this.setState({
      options: {
        ...this.state.options,
        accounts_integration_id: orderExportIntegrationId,
      },
    })
  }

  setOrderStatuses = (e, { value }) => {
    // The legacy backend sets statuses if they are in the root object
    // We omit all the statuses first and then add thoses that are selected
    this.setState({
      options: {
        ...omit(this.state.options, Object.values(STATUS_MAP)),
        ...value.reduce(
          (agg, status) => ({
            ...agg,
            [status]: true,
          }),
          {},
        ),
      },
    })
  }

  setOrderDateType = (e, { value }) => {
    // search_by is defined here
    // https://github.com/joor/web.php/blob/master/app/models/order.php#L1863
    this.setState({
      options: {
        ...this.state.options,
        search_by: value,
      },
    })
  }

  hasDateOptions = () => !!this.state.options.search_by

  formatStatus = (status) => {
    const STATUS_NAMES_MAP = {
      notes: messages.statusNotes,
      pending: messages.statusPending,
      approved: messages.statusApproved,
      shipped: messages.statusShipped,
      deleted: messages.statusCancelled,
    }

    return STATUS_NAMES_MAP[status]
  }

  exportOrders = () =>
    // TODO(ch15457) rebuild endpoint
    downloadFileFromResponse('/orders/export/', this.getRequestData())

  toggleOption = (e, { value }) => {
    const { options } = this.state
    const optionIsEnabled = options[value] && options[value] === 'true'

    this.setState({
      options: {
        ...options,
        [value]: optionIsEnabled ? 'false' : 'true',
      },
    })
  }

  renderRawDataOptions = () => (
    <List>
      <List.Item>
        <span className={classNames(styles.label)}>
          {messages.selectOrderStatus}
        </span>
        <Dropdown
          className={styles.dropdown}
          placeholder={messages.selectStatuses}
          options={this.getStatusesOptions()}
          defaultValue={this.getStatusesDefaults()}
          onChange={this.setOrderStatuses}
          multiple
        />
      </List.Item>
      <List.Item>
        <span className={classNames(styles.label, styles.section)}>
          {messages.selectDateType}
        </span>
        <Dropdown
          className={styles.dropdown}
          placeholder={messages.selectOne}
          options={this.getDateOptions()}
          onChange={this.setOrderDateType}
        />
      </List.Item>
      <List.Item>
        <DatePicker
          className={styles.datePicker}
          range
          singleDate
          disabled={!this.hasDateOptions()}
          placeholder={messages.addDateRange}
          onSubmit={this.setDate}
        />
      </List.Item>
      {this.renderIntegrationOptions()}
    </List>
  )

  renderExportOption = (exportOption) => {
    const { value, label, disabled } = exportOption
    const { options } = this.state
    const isSelected = options[value] && options[value] === 'true'

    return (
      <List.Item key={value} className={styles.checkbox}>
        <Checkbox
          label={label}
          value={value}
          checked={isSelected}
          disabled={disabled}
          onClick={this.toggleOption}
        />
      </List.Item>
    )
  }

  renderIntegrationOptions = () => (
    <div>
      <List.Item>
        <span className={classNames(styles.label, styles.section)}>
          {messages.exportType}
        </span>
        <Dropdown
          className={classNames(styles.dropdown, 'notranslate')}
          placeholder={messages.selectOne}
          options={this.getDropdownOptions()}
          onChange={this.setOrderExport}
          defaultValue={toGlobalId('OrderExportIntegration', '0')}
        />
      </List.Item>
      <span className={classNames(styles.label, styles.section)}>
        {messages.integrationOption}
      </span>
      {this.renderExportOption({
        label: messages.markExported,
        value: 'mark_exported',
        disabled: !this.props.allowMarkOrderExported,
      })}
      {this.renderExportOption({
        label: messages.excludePreviouslyExported,
        value: 'exclude_exported',
      })}
    </div>
  )

  render() {
    return (
      <Modal
        open
        onClose={this.props.onClose}
        primaryActionOnClick={this.onSave}
        primaryActionLabel={messages.export}
        primaryDisabled={
          !Object.keys(this.state.options).some((value) =>
            Object.values(STATUS_MAP).find((status) => value === status),
          )
        }
        title={messages.exportData}
        primaryActionId={ExportDataIds.ExportDataButton}
      >
        {this.renderRawDataOptions()}
      </Modal>
    )
  }
}

ExportAllDataModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  orderExportIntegrations: PropTypes.arrayOf(PropTypes.object),
  handleExportAction: PropTypes.func.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  allowMarkOrderExported: PropTypes.bool,
  trackExportAllDataModalSubmitted: PropTypes.func,
}

ExportAllDataModal.defaultProps = {
  orderExportIntegrations: [],
  allowMarkOrderExported: false,
  trackExportAllDataModalSubmitted: () => {},
}
